import { createFileRoute, Link, useNavigate, useRouter } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { type Profile } from "@/lib/people-constants";
import { ResidentProfileView } from "@/components/ResidentProfileView";

const PROFILE_COLS =
  "user_id, full_name, city, district, neighborhood, photo_url, profession, industry, industries, interests, languages, about, can_help_with, looking_for, looking_for_tags, coffee_status, role_tags, social_links, telegram, age, gender, origin_city, time_in_turkey, conversation_topic, username, has_seen_welcome, visitor_kind, created_at, expert_category, expert_categories, expert_offer, expert_formats, expert_area, expert_price_from, expert_price_currency, expert_link, expert_hidden, is_blogger, blog_url";

async function fetchProfile(userId: string) {
  const { data, error } = await supabase
    .from("profiles")
    .select(PROFILE_COLS)
    .eq("user_id", userId)
    .maybeSingle();
  if (error) throw error;
  if (!data) throw new Error("Профиль не найден");
  return data as Profile;
}

export const Route = createFileRoute("/_authenticated/people_/$userId")({
  head: () => ({
    meta: [
      { title: "Профиль резидента — TurkeyDrive" },
      { name: "description", content: "Профиль резидента сообщества TurkeyDrive." },
      { property: "og:title", content: "Профиль резидента — TurkeyDrive" },
      { property: "og:description", content: "Профиль резидента сообщества TurkeyDrive." },
      { property: "og:type", content: "profile" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  loader: async ({ context, params }) => {
    await context.queryClient.ensureQueryData({
      queryKey: ["people", params.userId],
      queryFn: () => fetchProfile(params.userId),
    });
  },
  errorComponent: ({ error }) => (
    <div className="min-h-screen bg-white px-4 py-12">
      <div className="mx-auto max-w-md text-center">
        <h1 className="text-xl font-semibold text-red-600">Не удалось открыть профиль</h1>
        <p className="mt-2 text-sm text-muted-foreground">{(error as Error).message}</p>
        <Link to="/people" className="mt-4 inline-block text-td-teal underline">Назад к людям</Link>
      </div>
    </div>
  ),
  notFoundComponent: () => (
    <div className="min-h-screen bg-white px-4 py-12 text-center text-td-muted">Профиль не найден.</div>
  ),
  component: PersonPage,
});

function PersonPage() {
  const { userId } = Route.useParams();
  const navigate = useNavigate();
  const router = useRouter();

  const { data: p } = useSuspenseQuery({
    queryKey: ["people", userId],
    queryFn: () => fetchProfile(userId),
  });

  const goBack = () => {
    if (router.history.canGoBack()) {
      router.history.back();
    } else {
      navigate({ to: "/people" });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <main className="mx-auto max-w-2xl px-5 py-6 md:px-6 md:py-10">
        <button
          type="button"
          onClick={goBack}
          className="mb-4 text-sm text-td-muted hover:text-td-dark"
        >
          ← Назад
        </button>


        <ResidentProfileView profile={p} />
      </main>
    </div>
  );
}
